/** Bulk Close on the Compliance board: one written outcome, one Close per selected open flag. */
import { inject, Injector } from '@angular/core';
import { ActivityEntityName, COMPLIANCE_BULK_CLOSE_CAP, ComplianceFlagBoardRow } from '@jms/model';
import { RightsService } from '@jms/utils/util.permissions';
import { closeFlags, promptFlagsOutcome } from '@workbuddy/entities/shared.flag/section.relation/flag.forms';
import { EntityDataService, EntityDataServiceRaw } from '@workbuddy/services/entities';
import { Command } from '@workbuddy/ui/model';
import { ToastService } from '@workbuddy/ui/services';
import { WorkBuddyDialogService } from '@workbuddy/ui/v2/blocks/dialog';
import type { BulkCommandArgs, BulkCommandOutcome, EntityBoardBulkCommandsArgs } from '@workbuddy/ui/v2/blocks/entity.board/context';
import { canActOnCompliance, isOpenCompliance } from './compliance.flag.command.support';

export const COMPLIANCE_CLOSE_BULK_ID = 'compliance.close.bulk';

type CloseRows = (rows: readonly ComplianceFlagBoardRow[]) => Promise<boolean>;

export function complianceFlagBulkCommands(args: EntityBoardBulkCommandsArgs<ComplianceFlagBoardRow>): Command[] {

  const rows = (args.selectedItems ?? []).filter(isOpenCompliance);
  if (!withinCap(rows) || !canActOnCompliance(inject(RightsService))) return [];
  const injector = inject(Injector);

  return [new Command({ _id: COMPLIANCE_CLOSE_BULK_ID, label: 'Close…', iconLeft: 'fa-regular fa-check', data: { run: selected => closeRows(injector, selected) } satisfies { run: CloseRows } })];
}

/** Unknown ids fall through, so metadata-provided bulk actions keep their default handling. */
export async function handleComplianceFlagBulkCommand(args: BulkCommandArgs<ComplianceFlagBoardRow>): Promise<BulkCommandOutcome | false> {

  const run = (args.commandData as { run?: CloseRows } | undefined)?.run;
  if (args.action !== COMPLIANCE_CLOSE_BULK_ID || !run) return false;
  const rows = args.data.filter(isOpenCompliance);
  if (!withinCap(rows)) return { handled: true, clearSelection: false };

  return { handled: true, clearSelection: await run(rows) };
}

/** A larger selection is a filter problem, not a bulk one: the cap guards against closing a backlog by mistake. */
function withinCap(rows: readonly ComplianceFlagBoardRow[]): boolean {

  return rows.length > 0 && rows.length <= COMPLIANCE_BULK_CLOSE_CAP;
}

/** Resolves true when anything closed, so the board clears the selection and rereads. */
async function closeRows(injector: Injector, rows: readonly ComplianceFlagBoardRow[]): Promise<boolean> {

  const outcome = await promptFlagsOutcome(injector.get(WorkBuddyDialogService), rows.length, { outcomeRequired: true });
  if (outcome === undefined) return false;
  const toasts = injector.get(ToastService);
  const { closed, failures } = await closeFlags(injector.get(EntityDataServiceRaw), rows, outcome);
  if (closed.length) injector.get(EntityDataService).invalidate(ActivityEntityName.ComplianceFlag);
  const plural = (count: number) => count === 1 ? '1 flag' : `${count} flags`;
  if (!failures.length) toasts.success('Close', `Closed ${plural(closed.length)}.`);
  else if (!closed.length) toasts.error('Close', `None of the selected flags could be closed: ${failures[0].message}`);
  else toasts.warning('Close', `Closed ${closed.length} of ${plural(rows.length)}. ${failures.length} could not be closed: ${failures[0].message}`);

  return closed.length > 0;
}
